import { Injectable } from "@angular/core";

import { TranslateService } from "@ngx-translate/core";

import { from } from "rxjs";
import {
  catchError,
  exhaustMap,
  map,
  mergeMap,
  switchMap,
  tap,
} from "rxjs/operators";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { AppState } from "../../reducers";
import * as DictionaryActionTypes from "../../actions/dictionary/dictionary.actions";
import * as MessagesActionTypes from "../../actions/messages/message.actions";

import { TableConfigService } from "src/app/shared/services/table-config/table-config.service";
import { PropertiesService } from "src/app/shared/services/properties/properties.service";
import { DictionariesService } from "src/app/shared/services/languages/dictionaries-service.service";

import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { SnackBarUtils } from "src/app/shared/utils/snackbarUtils";

@Injectable()
export class DictionaryEffects {
  // Dictionary List
  getDictionaryList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DictionaryActionTypes.getDictionaryList),
      switchMap((action) =>
        this.dictionaryService
          .getDictionaryList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              DictionaryActionTypes.loadDictionaryList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                DictionaryActionTypes.loadDictionaryList({
                  response: { result: [] } as any,
                }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Dictionary.loadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Dictionary Excel List
  getDictionaryExcel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DictionaryActionTypes.getDictionaryExcelList),
      exhaustMap((action) =>
        this.dictionaryService
          .getDictionaryList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              DictionaryActionTypes.loadDictionaryExcelList({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                DictionaryActionTypes.loadDictionaryExcelList({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Dictionary.loadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Dictioanry Properties
  getPropertiesForList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DictionaryActionTypes.getPropertiesForListByUsage),
      exhaustMap((action) =>
        this.propertiesService
          .getPropertiesByUsage(null, action.usageCode)
          .pipe(
            map((result) =>
              DictionaryActionTypes.loadPropertiesForListByUsage({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                DictionaryActionTypes.setLoadingDictionaryEndState(),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Dictionary.errorLoadProperties",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Dictionary Table Config
  getDictionaryTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DictionaryActionTypes.getDictionaryTableConfig),
      exhaustMap((action) =>
        this.tableConfigService
          .getConfigSettings(action.options.code, action.options.settingType)
          .pipe(
            map((result) =>
              DictionaryActionTypes.loadDictionaryTableConfig({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                DictionaryActionTypes.loadDictionaryTableConfig({
                  response: [],
                }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "TableConfig.LoadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Save Dictionary Table Config
  saveDictionaryTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DictionaryActionTypes.saveDictionaryTableConfig),
      exhaustMap((action) =>
        this.tableConfigService.saveConfigSettings(action.config).pipe(
          mergeMap(() => [
            DictionaryActionTypes.setLoadingDictionaryEndState(),
            MessagesActionTypes.setSnackbar(
              this.buildSnackbarObj(
                "TableConfig.SaveSuccess",
                SnackbarType.SUCCESS
              )
            ),
          ]),
          catchError((error: any) => {
            const actions = [
              DictionaryActionTypes.setLoadingDictionaryEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TableConfig.SaveError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getDictionary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DictionaryActionTypes.getDictionary),
      exhaustMap((action) =>
        this.dictionaryService
          .getDictionary(action)
          .pipe(
            map((result) =>
              DictionaryActionTypes.loadDictionary({ dictionary: result })
            ),
            catchError((error) => {
              const actions = [
                DictionaryActionTypes.setLoadingDictionaryEndState(),
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant("Dictionary.LoadError"),
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Properties by Usage
  getProperties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DictionaryActionTypes.getPropertiesByUsage),
      exhaustMap((action) =>
        this.propertiesService
          .getPropertiesByUsage(action.pageReq, action.usageCode)
          .pipe(
            map((result) =>
              DictionaryActionTypes.loadPropertiesByUsage({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                DictionaryActionTypes.setLoadingDictionaryEndState(),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Dictionary.errorLoadProperties",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Create Dictionary
  createDictionary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DictionaryActionTypes.createDictionary),
      exhaustMap((action) =>
        this.dictionaryService.createDictionary(action).pipe(
          mergeMap((result) => [
            DictionaryActionTypes.resetCreation(),
            DictionaryActionTypes.getDictionary({
              id: result?.result?.id,
            })
          ]),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Dictionary.dictionaryCreated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              DictionaryActionTypes.setLoadingDictionaryEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "Dictionary.dictionaryCreateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Update Dictionary
  updateDictionary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DictionaryActionTypes.updateDictionary),
      exhaustMap((action) =>
        this.dictionaryService.updateDictionary(action).pipe(
          map((result) => {
            return DictionaryActionTypes.getDictionary({
              id: result?.result?.id,
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Dictionary.dictionaryUpdated",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              DictionaryActionTypes.setLoadingDictionaryEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "Dictionary.dictionaryUpdateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Delete Dictionary
  deleteDictionary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DictionaryActionTypes.deleteDictionary),
      exhaustMap((action) =>
        this.dictionaryService.deleteDictionary(action).pipe(
          map((result) => {
            return DictionaryActionTypes.deletedDictionarySuccessfully();
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Dictionary.dictionaryDeleted",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              DictionaryActionTypes.setLoadingDictionaryEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Dictionary.dictionaryDeleteFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private dictionaryService: DictionariesService,
    private propertiesService: PropertiesService,
    private translate: TranslateService,
    private tableConfigService: TableConfigService
  ) {}

  buildSnackbarObj(message: string, snackbarType: SnackbarType) {
    return {
      snackbar: {
        message: this.translate.instant(message),
        type: snackbarType,
      },
    };
  }
}
